import Cookies from 'js-cookie';
import {
  ATC_SOURCE_COOKIE_NAME,
  ATC_SOURCE_COOKIE_MAX_AGE_DAYS,
  ATC_SOURCE_COOKIE_MAX_BYTES,
} from '@adapters/bigcommerce/constants';
import type { CartLineItems } from '@adapters/bigcommerce/types/cart';

/**
 * Which add-to-cart button an item was added from (ED-555). Sent as the GA4 item
 * parameter `atc_source` on `add_to_cart`, and persisted per cart line so checkout
 * can attach it to `begin_checkout` and `purchase`.
 *
 * Cross-repo contract: these values, the cookie names in
 * adapters/bigcommerce/constants.ts and the `{ [variantId]: source }` cookie shape
 * are mirrored in mec-checkout-js (packages/core/src/app/analytics/getAtcSources.ts).
 * Adding or renaming a value needs a matching change there, or checkout silently
 * drops it. The per-page values (e.g. 'clp bundle strip', ED-591) also double as
 * the line's `item_list_name` there, so they must match what add_to_cart sends.
 */
export const ATC_SOURCES = {
  PDP_MAIN: 'pdp_main',
  PDP_STICKY: 'pdp_sticky',
  PLP_QUICK_ADD: 'plp_quick_add',
  HOTSPOT: 'hotspot',
  HOMEPAGE_HOTSPOT: 'homepage hotspot',
  CIP_HOTSPOT: 'cip hotspot',
  CLP_HOTSPOT: 'clp hotspot',
  PDP_WHY_CHOOSE: 'pdp_why_choose',
  BUNDLE_STRIP: 'bundle_strip',
  HOMEPAGE_BUNDLE_STRIP: 'homepage bundle strip',
  CIP_BUNDLE_STRIP: 'cip bundle strip',
  CLP_BUNDLE_STRIP: 'clp bundle strip',
  HOMEPAGE_WHY_CHOOSE: 'homepage why choose',
  CIP_WHY_CHOOSE: 'cip why choose',
  CLP_WHY_CHOOSE: 'clp why choose',
} as const;

export type AtcSource = (typeof ATC_SOURCES)[keyof typeof ATC_SOURCES];

export type AtcSourceMap = Record<string, AtcSource>;

const ALLOWED_SOURCES = new Set<string>(Object.values(ATC_SOURCES));

/**
 * Parses a raw cookie value into a `{ [variantId]: source }` map, dropping anything
 * that isn't one of the allowed values — the cookie is client-writable and checkout
 * pushes these straight into the dataLayer.
 */
export const parseAtcSources = (raw: string | undefined): AtcSourceMap => {
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, AtcSource] =>
          typeof entry[1] === 'string' && ALLOWED_SOURCES.has(entry[1])
      )
    );
  } catch {
    return {};
  }
};

/**
 * Narrows a source map to the variants actually in the cart, so checkout never sees
 * a stale entry for something that was removed (or added since by an untagged path).
 */
export const filterAtcSourcesToVariants = (
  sources: AtcSourceMap,
  variantIds: number[]
): AtcSourceMap => {
  const inCart = new Set(variantIds.map(String));
  return Object.fromEntries(
    Object.entries(sources).filter(([variantId]) => inCart.has(variantId))
  );
};

/** Variant ids of the cart's physical and digital lines (the ones with variants). */
export const getCartVariantIds = (lineItems?: CartLineItems): number[] =>
  [
    ...(lineItems?.physical_items ?? []),
    ...(lineItems?.digital_items ?? []),
  ].map((item) => item.variant_id);

/** Total quantity of a variant across the cart's lines (0 when absent). */
export const getVariantQuantity = (
  lineItems: CartLineItems | undefined,
  variantId: number
): number =>
  [...(lineItems?.physical_items ?? []), ...(lineItems?.digital_items ?? [])]
    .filter((item) => item.variant_id === variantId)
    .reduce((total, item) => total + item.quantity, 0);

// `encodeURIComponent` is an upper bound on js-cookie's encoding of the value.
const encodedCookieBytes = (sources: AtcSourceMap) =>
  ATC_SOURCE_COOKIE_NAME.length +
  1 +
  encodeURIComponent(JSON.stringify(sources)).length;

/**
 * Persists the map: optionally narrowed to the cart's variants first, then capped
 * to ATC_SOURCE_COOKIE_MAX_BYTES so a write can never be silently rejected for size.
 * The cap only bites for carts with 100+ distinct variants; it drops other entries
 * (in key order — variant-id keys don't keep insertion order) and never `keepKey`.
 */
const writeAtcSources = (
  sources: AtcSourceMap,
  cartVariantIds?: number[],
  keepKey?: string
) => {
  const bounded = cartVariantIds
    ? filterAtcSourcesToVariants(sources, cartVariantIds)
    : { ...sources };

  while (encodedCookieBytes(bounded) > ATC_SOURCE_COOKIE_MAX_BYTES) {
    const dropKey = Object.keys(bounded).find((key) => key !== keepKey);
    if (!dropKey) break;
    delete bounded[dropKey];
  }

  if (Object.keys(bounded).length === 0) {
    Cookies.remove(ATC_SOURCE_COOKIE_NAME);
    return;
  }

  Cookies.set(ATC_SOURCE_COOKIE_NAME, JSON.stringify(bounded), {
    expires: ATC_SOURCE_COOKIE_MAX_AGE_DAYS,
  });
};

/**
 * Records the button a variant was just added from — call only when units were
 * actually added. An add with no source (save for later, delivery-method switch,
 * anything untagged) clears the variant's entry, so the latest add always wins and
 * untagged adds omit the param.
 *
 * Pass the cart's variant ids (from the add response) to drop entries for anything
 * no longer in the cart. That keeps the cookie no larger than the cart; otherwise it
 * would only grow (each write renews the expiry) until it passed the ~4 KB cookie
 * limit and browsers started silently rejecting it.
 */
export const recordAtcSource = (
  variantId: number,
  source?: AtcSource,
  cartVariantIds?: number[]
) => {
  const sources = parseAtcSources(Cookies.get(ATC_SOURCE_COOKIE_NAME));
  const key = String(variantId);

  if (source) {
    sources[key] = source;
  } else {
    delete sources[key];
  }

  writeAtcSources(sources, cartVariantIds, source ? key : undefined);
};

/**
 * For an add that added nothing (an error, or a stock-capped no-op): keep every
 * existing source — including the variant's previous one — and only drop entries
 * for variants that are no longer in the cart.
 */
export const pruneAtcSources = (cartVariantIds: number[]) => {
  const raw = Cookies.get(ATC_SOURCE_COOKIE_NAME);
  if (!raw) return;

  writeAtcSources(parseAtcSources(raw), cartVariantIds);
};
